import React, { useContext, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faArrowUp, faArrowDown, faCode } from '@fortawesome/free-solid-svg-icons';
import './styles/CustomizedVariable.css'; 
import axios from 'axios';
import config from '../configs/config';
import VariableContext from '../contexts/VariableContext';

const CustomizedVariable = ({ isOpen, onClose, setcurrentSelectedVariablesset, projectId }) => {
  const { dataGroup, setdataGroup } = useContext(VariableContext);
  const [rows, setRows] = useState([{ stub: '', logic: '' }]);
  const [variableName, setVariableName] = useState('');
  const [availableVariables, setAvailableVariables] = useState([]);
  const [showVariableSelector, setShowVariableSelector] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(0);
  const [operationType, setOperationType] = useState('OR');
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [advancedMode, setAdvancedMode] = useState(false);

  useEffect(() => {
    // Extract available variables from dataGroup when component mounts
    if (dataGroup) {
      setAvailableVariables(Object.keys(dataGroup).filter(v => !v.startsWith('_')));
    }
  }, [dataGroup]);

  if (!isOpen) return null;

  const handleAddRow = () => {
    setRows([...rows, { stub: '', logic: '' }]);
  };

  const handleRemoveRow = (index) => {
    if (rows.length > 1) {
      const newRows = [...rows];
      newRows.splice(index, 1);
      setRows(newRows);
    }
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const newRows = [...rows];
      [newRows[index], newRows[index - 1]] = [newRows[index - 1], newRows[index]];
      setRows(newRows);
    }
  };

  const handleMoveDown = (index) => {
    if (index < rows.length - 1) {
      const newRows = [...rows];
      [newRows[index], newRows[index + 1]] = [newRows[index + 1], newRows[index]];
      setRows(newRows);
    }
  };

  const handleInputChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const openVariableSelector = (index) => {
    setCurrentRowIndex(index);
    setShowVariableSelector(true);
    setSelectedVariables([]);
  };

  const toggleVariableSelection = (variable) => {
    setSelectedVariables(prev => 
      prev.includes(variable) 
        ? prev.filter(v => v !== variable) 
        : [...prev, variable]
    );
  };

  const applyVariableOperation = () => {
    if (selectedVariables.length === 0) return;
    
    let logicExpression = '';
    if (advancedMode) {
      // In advanced mode, just use the current logic
      logicExpression = rows[currentRowIndex].logic;
    } else {
      // In simple mode, build the expression based on selected variables and operation
      const operator = operationType === 'OR' ? ' or ' : 
                      operationType === 'AND' ? ' and ' : 
                      ' and not ';
      
      logicExpression = selectedVariables
        .map((v, i) => {
          if (operationType === 'NOT' && i === selectedVariables.length - 1) {
            return `not ${v}`;
          }
          return v;
        })
        .join(operator);
    }

    const newRows = [...rows];
    newRows[currentRowIndex].logic = logicExpression;
    setRows(newRows);
    setShowVariableSelector(false);
  };

  const handleSave = () => {
    const customVariable = {
      name: variableName,
      type: 'SingleResponse',
      logic: rows.map(row => ({
        stub: row.stub,
        condition: row.logic
      }))
    };

    try {
      if (!customVariable?.name) {
        throw new Error("Custom variable name is missing");
      }
      
      setcurrentSelectedVariablesset(prev => [...prev, customVariable.name]);
      
      try {
        const response = axios.post(`${config.API_BASE_URL}/compute-newvar`, {
          projectId: projectId,
          customVariable: customVariable
        });

        if (response.data.success) {
          alert(response.data.message);
        }
      } catch (err) {
        console.error('Error creating custom variable:', err);
      } finally {
        const valueLabels = {};
        const values = rows.map((row, index) => {
          const value = (index + 1).toString() + ".0";
          valueLabels[value] = row.stub;
          return {
            index: index + 1,
            title: row.stub
          };
        });

        setdataGroup({
          ...dataGroup,
          [variableName]: {
            "Question number": variableName,
            Qtitle: variableName,
            instructions: "",
            type: "SingleResponse",
            values: values,
            valueLabels: valueLabels,
            varName: [{ Name: variableName }],
            Cols: [],
            Rows: [],
            columns: [],
            options: [],
            SE: false,
            SPSSSyntax: false,
            STD: false,
            conceptTables: false,
            error: false,
            customeVar: true,
            grid: {
              selected: false,
              type: ["Multiple3DGrid", "Single2DGrid", "Single3DGrid", "Numeric2DGrid", "Numeric3DGrid", "Multiple4DGrid"]
            },
            mean: false,
            median: false,
            order: Object.keys(dataGroup).length + 1,
            rank: false,
            rankCol: false,
            rankSumary: {
              selected: false,
              type: ["Multiple3DGrid", "Single2DGrid", "Single3DGrid", "Numeric2DGrid", "Numeric3DGrid", "Multiple4DGrid"]
            },
            rankalphabetical: false,
            removeTab: false,
            reverseGrid: {
              selected: false,
              type: ["Multiple3DGrid", "Single2DGrid", "Single3DGrid", "Numeric2DGrid", "Numeric3DGrid", "Multiple4DGrid"]
            },
            table: "freq"
          }
        });
      }
      alert("Custom variable created successfully!");
    } catch (error) {
      console.error('Error creating custom variable:', error);
      alert(error.message || "Error creating custom variable");
    }

    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Custom Variable</h3>
          <button onClick={onClose} className="modal-close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="variable-name-input">
          <label>Variable Name:</label>
          <input
            type="text"
            value={variableName}
            onChange={(e) => setVariableName(e.target.value)}
            placeholder="Enter variable name"
          />
        </div>

        <div className="table-container">
          <table className="custom-var-table">
            <thead>
              <tr>
                <th>Group Label</th>
                <th>Logic Definition</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="text"
                      value={row.stub}
                      onChange={(e) => handleInputChange(index, 'stub', e.target.value)}
                      placeholder="Group label"
                    />
                  </td>
                  <td>
                    <div className="logic-input-container">
                      <input
                        type="text"
                        value={row.logic}
                        onChange={(e) => handleInputChange(index, 'logic', e.target.value)}
                        placeholder="e.g., Q1 or Q2"
                      />
                      <button 
                        className="variable-selector-btn"
                        onClick={() => openVariableSelector(index)}
                        title="Select variables"
                      >
                        <FontAwesomeIcon icon={faCode} />
                      </button>
                    </div>
                  </td>
                  <td className="actions-cell">
                    <button 
                      onClick={() => handleMoveUp(index)} 
                      disabled={index === 0}
                      title="Move up"
                    >
                      <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                    <button 
                      onClick={() => handleMoveDown(index)} 
                      disabled={index === rows.length - 1}
                      title="Move down"
                    >
                      <FontAwesomeIcon icon={faArrowDown} />
                    </button>
                    <button 
                      onClick={() => handleRemoveRow(index)} 
                      disabled={rows.length <= 1}
                      title="Remove row"
                      className="remove-btn"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="modal-footer">
          <button onClick={handleAddRow} className="add-row-btn">
            <FontAwesomeIcon icon={faPlus} /> Add Row
          </button>
          <button onClick={handleSave} className="save-btn">
            Save Custom Variable
          </button>
        </div>

        {/* Variable Selector Modal */}
        {showVariableSelector && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content">
              <h3>Combine Variables</h3>
              
              <div className="operation-selector">
                <label>
                  <input
                    type="radio"
                    checked={!advancedMode}
                    onChange={() => setAdvancedMode(false)}
                  />
                  Simple Mode
                </label>
                <label>
                  <input
                    type="radio"
                    checked={advancedMode}
                    onChange={() => setAdvancedMode(true)}
                  />
                  Advanced Mode
                </label>
              </div>

              {!advancedMode ? (
                <>
                  <div className="operation-type">
                    <select 
                      value={operationType} 
                      onChange={(e) => setOperationType(e.target.value)}
                    >
                      <option value="OR">OR (Any of the selected)</option>
                      <option value="AND">AND (All of the selected)</option>
                      <option value="NOT">AND NOT (All except last)</option>
                    </select>
                  </div>

                  <div className="variable-list">
                    {availableVariables.map(variable => (
                      <div 
                        key={variable} 
                        className={`variable-item ${selectedVariables.includes(variable) ? 'selected' : ''}`}
                        onClick={() => toggleVariableSelection(variable)}
                      >
                        {variable}
                      </div>
                    ))}
                  </div>

                  <div className="preview-section">
                    <h4>Preview:</h4>
                    <div className="preview-logic">
                      {selectedVariables.length > 0 ? (
                        selectedVariables.map((v, i) => (
                          <React.Fragment key={v}>
                            {i > 0 && (
                              <span className="operator">
                                {operationType === 'OR' ? 'OR' : 
                                 operationType === 'AND' ? 'AND' : 
                                 i === selectedVariables.length - 1 ? 'AND NOT' : 'AND'}
                              </span>
                            )}
                            <span className="variable">{v}</span>
                          </React.Fragment>
                        ))
                      ) : (
                        <span className="no-variables">No variables selected</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="advanced-mode">
                  <h4>Advanced Logic Builder</h4>
                  <textarea
                    value={rows[currentRowIndex].logic}
                    onChange={(e) => handleInputChange(currentRowIndex, 'logic', e.target.value)}
                    placeholder="Enter custom logic (e.g., (Q1 or Q2) and not Q3)"
                    rows={5}
                  />
                  <div className="available-vars-hint">
                    <p>Available variables: {availableVariables.join(', ')}</p>
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => setShowVariableSelector(false)} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={applyVariableOperation} className="apply-btn">
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomizedVariable;